<template>
    <div>
        <div class="draft-title-container">
            <h2 class="draft-title">
                {{ $trans("web_profile.draft_in_progress") || "Draft (in progress):" }}
            </h2>
            <el-tooltip placement="right" effect="light">
                <template #content>
                    <div class="draft-tooltip-content">
                        {{ $trans("web_profile.draft_info") }}
                    </div>
                </template>
                <el-icon class="draft-info-icon"><InfoFilled /></el-icon>
            </el-tooltip>
        </div>

        <section class="card profile-header-section">
            <div class="avatar-column">
                <img
                    :src="WebProfileStore.profile.photo"
                    alt="Avatar"
                    class="profile-avatar"
                />
                <div class="switcher-wrapper">
                    <span class="switcher-label">
                        {{ $trans("profile_dossier.use_standard_photo") }}
                    </span>
                    <el-switch
                        v-model="WebProfileStore.useDefaultPhoto"
                        @change="handleSwitchChange"
                        inline-prompt
                        :active-text="$trans('messages.yes')"
                        :inactive-text="$trans('messages.no')"
                        style="
                            --el-switch-on-color: #41abc4;
                            --el-switch-off-color: #999;
                        "
                    />

                    <el-button
                        type="primary"
                        class="upload-photo-btn"
                        :disabled="WebProfileStore.useDefaultPhoto"
                        @click="showUploadModal = true"
                    >
                        <el-icon class="mr-2"><Upload /></el-icon>
                        {{
                            $trans("profile_dossier.upload_photo") || "Upload Photo"
                        }}
                    </el-button>
                </div>
            </div>

            <div class="details-column">
                <div v-if="isAiProcessing" class="ai-alert">
                    <el-icon class="is-loading mr-2"><Loading /></el-icon>
                    <span>{{ $trans("web_profile.ai_in_progress") || "AI is generating content. Editing is temporarily disabled." }}</span>
                </div>

                <div class="details-header">
                    <div
                        v-if="WebProfileStore.profile.claim || !isGenerating"
                        style="white-space: pre-wrap"
                    >
                        <h2 v-if="WebProfileStore.profile.claim">
                            {{ WebProfileStore.profile.claim }}
                        </h2>
                        <span v-else class="empty-text">
                            {{ $trans("web_profile.add_claim") || "Add Claim" }}
                        </span>
                    </div>

                    <el-skeleton
                        v-if="isGenerating && !WebProfileStore.profile.claim"
                        animated
                        :rows="1"
                        style="width: 60%; margin-bottom: 10px"
                    />

                    <div class="button-group">
                        <el-button
                            type="primary"
                            size="small"
                            :loading="isGenerating"
                            :disabled="isAiProcessing"
                            @click="showGenerateModal = true"
                            class="generate-btn"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                class="mr-2"
                            >
                                <path
                                    d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"
                                />
                            </svg>
                            {{
                                $trans("web_profile.generate_claim") ||
                                "AI Generate"
                            }}
                        </el-button>
                        
                        <LogButton 
                            v-if="WebProfileStore.isAdmin" 
                            :onClick="() => WebProfileStore.openVersionHistoryModal('claim')" 
                        />

                        <EditButton 
                            :onClick="isAiProcessing ? () => {} : WebProfileStore.openEditClaimModal" 
                            :class="{ 'disabled-action': isAiProcessing }"
                        />
                    </div>
                </div>

                <div class="competences-header">
                    <h3>{{ $trans("web_profile.top_competences") }}</h3>
                    <EditButton
                        :onClick="WebProfileStore.openEditCompetencesModal"
                    />
                </div>

                <div class="profile-card-competencies">
                    <template
                        v-for="comp in WebProfileStore.topCompetences.slice(0, 3)"
                        :key="comp.id"
                    >
                        <div class="profile-card-competencies-item">
                            <div class="title">
                                {{ comp.name }} /
                                <span class="title-duration">{{
                                    comp.duration
                                }}</span>
                            </div>
                            <ProgressBar
                                :percentage="comp.duration_percentage"
                                :tag-id="comp.id"
                            />
                        </div>
                    </template>
                </div>

                <div class="info-row">
                    <div class="info-item">
                        <div
                            class="availability-status"
                            :class="
                                availabilityClass(
                                    WebProfileStore.profile.availability,
                                )
                            "
                        ></div>
                        <span>
                            {{
                                $trans(
                                    "web_profile.availability_status_types." +
                                        WebProfileStore.profile.availability,
                                )
                            }}
                        </span>
                    </div>

                    <div class="info-item">
                        <el-icon class="location-icon icon-align"
                            ><Location
                        /></el-icon>
                        <strong>{{ $trans("web_profile.region") }}:&nbsp;</strong>
                        
                        <template v-if="['CH', 'DE'].includes(WebProfileStore.profile.country)">
                            {{ WebProfileStore.profile.region }}
                        </template>
                        <template v-else>
                            {{ WebProfileStore.profile.custom_region || WebProfileStore.profile.country_name }}
                            <EditButton
                                v-if="WebProfileStore.isAdmin"
                                :onClick="WebProfileStore.openEditRegionModal"
                                class="ml-2"
                            />
                        </template>
                    </div>

                    <div class="info-item">
                        <strong class="label-with-tooltip">
                            {{
                                $trans("web_profile.industry_sector") ||
                                "Industry Sector"
                            }}*
                            <el-tooltip placement="top" effect="light">
                                <template #content>
                                    <div
                                        style="
                                            max-width: 300px;
                                            font-size: 16px;
                                            font-weight: 600;
                                        "
                                    >
                                        {{
                                            $trans(
                                                "web_profile.industry_tooltip",
                                            ) || "Industry Sector"
                                        }}
                                    </div>
                                </template>
                                <el-icon class="info-icon"><InfoFilled /></el-icon>
                            </el-tooltip>
                            :&nbsp;
                        </strong>

                        <span v-if="WebProfileStore.webProfile.verticals_name">
                            {{ WebProfileStore.webProfile.verticals_name }}
                        </span>
                        <span v-else class="empty-text">
                            {{ $trans("web_profile.please_select_verticals") }}
                        </span>

                        <EditButton
                            :onClick="WebProfileStore.openEditVerticalsModal"
                            class="ml-2"
                        />
                    </div>

                    <div
                        class="info-item"
                        :class="{
                            'opacity-50':
                                !WebProfileStore.webProfile.verticals_name,
                        }"
                    >
                        <strong class="label-with-tooltip">
                            {{ $trans("web_profile.area_of_expertise") }}*
                            <el-tooltip placement="top" effect="light">
                                <template #content>
                                    <div
                                        style="
                                            max-width: 300px;
                                            font-size: 16px;
                                            font-weight: 600;
                                        "
                                    >
                                        {{
                                            $trans("web_profile.aoe_tooltip") ||
                                            "Industry Sector"
                                        }}
                                    </div>
                                </template>
                                <el-icon class="info-icon"><InfoFilled /></el-icon>
                            </el-tooltip>
                            :&nbsp;
                        </strong>

                        <span
                            v-if="!WebProfileStore.webProfile.verticals_name"
                            class="empty-text"
                        >
                            {{
                                $trans("web_profile.please_select_aoe") ||
                                "Please select Industry Sector first"
                            }}
                        </span>
                        <span
                            v-else-if="
                                WebProfileStore.webProfile.area_of_expertise_name
                            "
                        >
                            {{ WebProfileStore.webProfile.area_of_expertise_name }}
                        </span>
                        <span v-else class="empty-text">
                            {{ $trans("web_profile.please_select_aoe") }}
                        </span>

                        <EditButton :onClick="handleAoEEdit" class="ml-2" />
                    </div>
                </div>
            </div>

            <EditClaimModal />
            <EditTopCompetencesModal />
            <EditAoEModal />
            <EditVerticalsModal />
            <EditRegionModal />
            <VersionHistoryModal />

            <ConfirmClaimGenerationModal
                v-model="showGenerateModal"
                @confirm="generateAndSaveClaim"
            />

            <UploadPhotoModal
                v-model="showUploadModal"
                :profile-id="WebProfileStore.profile.id"
                @photo-updated="handlePhotoUpdated"
            />
        </section>
    </div>
</template>

<script>
import { mapStores } from "pinia";
import { useWebProfileStore } from "@/stores/WebProfileStore";
import {
    Edit,
    Loading,
    Location,
    Upload,
    InfoFilled,
} from "@element-plus/icons-vue";
import EditButton from "./../Components/EditButton.vue";
import LogButton from "./../Components/LogButton.vue";
import ProgressBar from "./../Components/ProgressBar.vue";
import EditClaimModal from "./Modals/EditClaimModal.vue";
import EditTopCompetencesModal from "./Modals/EditTopCompetencesModal.vue";
import EditAoEModal from "./Modals/EditAoEModal.vue";
import EditVerticalsModal from "./Modals/EditVerticalsModal.vue";
import EditRegionModal from "./Modals/EditRegionModal.vue";
import ConfirmClaimGenerationModal from "./Modals/ConfirmClaimGenerationModal.vue";
import UploadPhotoModal from "./Modals/UploadPhotoModal.vue";
import VersionHistoryModal from "./Modals/VersionHistoryModal.vue";
import axios from "axios";

export default {
    name: "WebProfileHeader",
    components: {
        ProgressBar,
        EditClaimModal,
        EditTopCompetencesModal,
        EditAoEModal,
        EditVerticalsModal,
        EditRegionModal,
        ConfirmClaimGenerationModal,
        UploadPhotoModal,
        VersionHistoryModal,
        Edit,
        Loading,
        Location,
        Upload,
        InfoFilled,
        EditButton,
        LogButton,
    },
    data() {
        return {
            isGenerating: false,
            showGenerateModal: false,
            showUploadModal: false,
            pollingInterval: null,
        };
    },
    computed: {
        ...mapStores(useWebProfileStore),
        hasSummary() {
            const summary = this.WebProfileStore.profile?.summary;
            return typeof summary === "string" && summary.trim().length > 0;
        },
        isAiProcessing() {
            return this.isGenerating || this.WebProfileStore?.aiContentStatus === 'in_progress';
        }
    },
    watch: {
        async "WebProfileStore.webProfile.verticals_name"(newVal, oldVal) {
            if (oldVal && newVal !== oldVal) {
                this.WebProfileStore.webProfile.area_of_expertise_name = null;
                this.WebProfileStore.webProfile.area_of_expertise_id = null;
                this.WebProfileStore.webProfile.area_of_expertise = [];

                try {
                    this.$functions.showLoader();
                    await this.WebProfileStore.saveDomainSection(
                        this.WebProfileStore.profile.id,
                        "aoe"
                    );
                } catch (e) {
                    this.$message.error(this.$trans("global.something_went_wrong"));
                } finally {
                    this.$functions.hideLoader();
                }
            }
        },
    },
    mounted() {
        this.startAiStatusPolling();
    },
    beforeUnmount() {
        this.stopAiStatusPolling();
    },
    methods: {
        startAiStatusPolling() {
            this.pollingInterval = setInterval(async () => {
                if (this.WebProfileStore.aiContentStatus === 'in_progress') {
                    try {
                        const profileId = this.WebProfileStore.profile.id;
                        const response = await axios.get(`/candidates/${profileId}/web-profile/ai-status`);
                        if (response.data && response.data.ai_content_status) {
                            if (response.data.ai_content_status === 'success') {
                                this.WebProfileStore.updateGeneratedContent(response.data);
                            } else {
                                this.WebProfileStore.aiContentStatus = response.data.ai_content_status;
                            }
                        }
                    } catch (e) {
                        console.error(e);
                    }
                }
            }, 5000);
        },
        stopAiStatusPolling() {
            if (this.pollingInterval) {
                clearInterval(this.pollingInterval);
            }
        },
        handlePhotoUpdated(newPhotoBase64) {
            this.WebProfileStore.profile.photo = newPhotoBase64;
        },
        handleAoEEdit() {
            if (!this.WebProfileStore.webProfile.verticals_name) {
                this.$message.warning(
                    this.$trans("web_profile.select_vertical_first") ||
                        "Please select Industry Sector first."
                );
                return;
            }
            this.WebProfileStore.openEditAoEModal();
        },
        async handleSwitchChange(useDefault) {
            const token = document.head.querySelector('meta[name="csrf-token"]')?.content;
            try {
                this.$functions.showLoader();
                const profileId = this.WebProfileStore.profile.id;
                const response = await axios.post(
                    `/candidates/${profileId}/web-profile/set-default-image`,
                    { use_default_photo: useDefault },
                    {
                        headers: {
                            "X-CSRF-TOKEN": token,
                            "Content-Type": "application/json",
                        },
                    }
                );
                if (response.data?.base64) {
                    this.WebProfileStore.profile.photo = response.data.base64;
                    this.WebProfileStore.useDefaultPhoto = useDefault;
                    this.$message.success(
                        useDefault
                            ? this.$trans("profile_dossier.photo_set_to_default_successfully")
                            : this.$trans("profile_dossier.photo_set_to_custom")
                    );
                }
            } catch (error) {
                console.error(error);
            } finally {
                this.$functions.hideLoader();
            }
        },
        async generateAndSaveClaim() {
            if (this.isGenerating) return;

            if (!this.hasSummary) {
                this.$message.error(
                    this.$trans("web_profile.generate_summary_first") ||
                        "You must generate Summary first."
                );
                return;
            }

            this.isGenerating = true;
            this.WebProfileStore.aiContentStatus = 'in_progress';

            try {
                const profileId = this.WebProfileStore.profile.id;
                if (!profileId) return;

                const token = document.head.querySelector('meta[name="csrf-token"]')?.content;

                await axios.post(
                    `/candidates/${profileId}/web-profile/generate/claim`,
                    {},
                    {
                        headers: {
                            "X-CSRF-TOKEN": token,
                            "Content-Type": "application/json",
                        },
                    }
                );

                this.$message.success(
                    this.$trans("web_profile.generation_started_email_notification") || 
                    "Generation started. You will be notified via email when it's done."
                );

                this.showGenerateModal = false;
            } catch (error) {
                if (error.response && error.response.status === 429) {
                    this.$message.warning(
                        this.$trans("web_profile.ai_busy") ||
                            "AI is busy, please try again later."
                    );
                } else if (error.response && error.response.status === 423) {
                    this.$message.warning(
                        this.$trans("web_profile.generation_locked") ||
                            "Content generation for this section is currently in progress. Please try again later."
                    );
                } else {
                    console.error("Error starting claim generation:", error);
                    this.$message.error("AI server error");
                }
            } finally {
                this.isGenerating = false;
            }
        },
        availabilityClass(availability) {
            return {
                "border-availability-available": availability == "available",
                "border-availability-on-request": availability == "on_request",
                "border-availability-unavailable": availability == "unavailable",
            };
        },
    },
};
</script>

<style scoped>
.draft-title-container {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
}

.draft-title {
    font-size: 1.5rem;
    font-weight: 600;
    color: #333;
    margin: 0;
}

.draft-info-icon {
    color: #41abc4;
    font-size: 18px;
    cursor: pointer;
}

.draft-tooltip-content {
    max-width: 320px;
    font-size: 14px;
    line-height: 1.5;
}

.opacity-50 {
    opacity: 0.5;
}

.disabled-action {
    opacity: 0.5;
    pointer-events: none;
    cursor: not-allowed !important;
}

.ai-alert {
    background-color: #e6f7ff;
    border: 1px solid #91d5ff;
    padding: 10px 16px;
    border-radius: 4px;
    margin-bottom: 16px;
    display: flex;
    align-items: center;
    color: #1890ff;
    font-size: 14px;
    font-weight: 500;
}

.loading-claim {
    display: flex;
    align-items: center;
    gap: 8px;
}
.button-group {
    display: flex;
    align-items: center;
    gap: 8px;
}

.generate-btn {
    background-color: #41abc4;
    border-color: #41abc4;
    color: white;
    font-weight: 500;
}
.generate-btn:hover {
    background-color: #3693a9;
    border-color: #3693a9;
}

.generate-btn.is-disabled,
.generate-btn.is-disabled:hover,
.generate-btn.is-disabled:focus,
.generate-btn.is-disabled:active {
    background-color: #999 !important;
    border-color: #999 !important;
    cursor: not-allowed !important;
    pointer-events: auto !important;
}

.el-button.is-disabled {
    background-color: #999 !important;
    border-color: #999 !important;
    cursor: not-allowed !important;
    pointer-events: auto !important;
}

.profile-header-section {
    display: grid;
    grid-template-columns: 180px 1fr;
    grid-template-rows: auto auto;
    gap: 1rem 2rem;
    padding: 1.5rem;
    border: 1px solid #ddd;
    border-radius: 8px;
    background-color: #fff;
}

.profile-avatar {
    border-radius: 50%;
    width: 150px;
    height: 150px;
    object-fit: cover;
    border: 3px solid #f0f0f0;
    background-color: #AA916A;
}

.details-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid #eee;
    padding-bottom: 0.5rem;
}

.competences-header {
    margin-top: 0.5rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-bottom: 0.5rem;
}

.profile-card-competencies {
    margin-top: 1rem;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
}

.profile-card-competencies-item .title {
    font-weight: 700;
    color: #000;
    margin-bottom: 0.25rem;
    font-size: 16px;
}

.info-item {
    font-size: 0.95rem;
    color: #000;
    display: flex;
    align-items: center;
    white-space: nowrap;
}

.info-item strong {
    color: #000;
}

.btn-sm {
    padding: 0.25rem 0.5rem;
    font-size: 0.8rem;
}
.btn-outline-secondary {
    border-color: #ccc;
    color: #555;
    background: #fff;
    cursor: pointer;
    border-radius: 4px;
}
.btn-outline-secondary:hover {
    background-color: #f8f8f8;
}

.avatar-column {
    grid-row: 1 / span 2;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
}

.switcher-wrapper {
    margin-top: 1rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
    width: 100%;
}

.upload-photo-btn {
    width: 100%;
    margin-top: 0.5rem;
    background-color: #41abc4;
    border-color: #41abc4;
}

.upload-photo-btn:hover {
    background-color: #3693a9;
    border-color: #3693a9;
}

.upload-photo-btn.is-disabled,
.upload-photo-btn.is-disabled:hover {
    background-color: #999;
    border-color: #999;
    cursor: not-allowed !important;
    pointer-events: auto !important;
}

.switcher-label {
    font-size: 0.9rem;
    color: #000;
    text-align: center;
    display: block;
}

.details-column {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    gap: 1rem;
    width: 100%;
}

.details-header h2 {
    margin: 0;
    font-size: 1.875rem;
    font-weight: 600;
}

.competences-header h3 {
    margin: 0;
    font-size: 1.875rem;
    font-weight: 600;
}

.info-row {
    display: flex;
    flex-wrap: wrap;
    gap: 1.5rem 2rem;
    margin-top: 1.5rem;
    padding-top: 1rem;
    border-top: 1px solid #eee;
}

.el-button {
    height: 34px;
    font-size: 16px;
    font-weight: 700 !important;
}

.border-availability-available {
    border-color: #41abc4;
}

.border-availability-on-request {
    border-color: #ef9630;
}

.border-availability-unavailable {
    border-color: #ff6b65;
}

.availability-status {
    width: 0;
    height: 0;
    border-radius: 50%;
    border-width: 12px;
    border-style: solid;
    margin-right: 5px;
}

.location-icon {
    margin-right: 4px;
    width: 26px;
    height: 16px;
}

.location-icon :deep(svg) {
    width: 26px;
    height: 25px;
}

.mr-2 {
    margin-right: 8px;
}
.ml-2 {
    margin-left: 8px;
}

.empty-text {
    color: #f56c6c;
    font-size: 0.9rem;
    margin-right: 5px;
}

.label-with-tooltip {
    display: inline-flex;
    align-items: center;
    font-weight: 600;
    font-size: 16px;
}

.info-icon {
    font-size: 16px;
    margin-left: 4px;
    color: #999;
    cursor: help;
    color: #41abc4;
}
.icon-align {
    vertical-align: middle;
    margin-top: 4px;
}
</style>